import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Volume2, VolumeX, Eye, EyeOff, Clock } from 'lucide-react';
import { WorkoutTimer } from './WorkoutTimer';
import { WorkoutControls } from './WorkoutControls';
import { WorkoutProgress } from './WorkoutProgress';
import { ExerciseDetails } from './ExerciseDetails';
import { HeartRateZoneMeter } from '../../HeartRateZoneMeter';
import { ActivityReport } from '../../activities/ActivityReport';
import { simulateHeartRate, calculateStrain } from '../../../utils/workout';
import { CircularProgress } from '../../CircularProgress';

interface Exercise {
  name: string;
  duration: number;
  completed: boolean;
  description?: string;
  intensity?: string;
}

interface WorkoutPlayer2Props {
  workout: {
    id: string;
    name: string;
    exercises: Exercise[];
    targetStrain?: number;
  };
  onClose: () => void;
}

const heartRateZones = [
  { id: 0, name: 'Rest', min: 0, max: 90, color: 'text-muted-foreground', description: 'Recovery & Rest' },
  { id: 1, name: 'Zone 1', min: 91, max: 110, color: 'text-info', description: 'Warm Up & Cool Down' },
  { id: 2, name: 'Zone 2', min: 111, max: 124, color: 'text-success', description: 'Fat Burn & Endurance' },
  { id: 3, name: 'Zone 3', min: 125, max: 135, color: 'text-warning', description: 'Aerobic Power' },
  { id: 4, name: 'Zone 4', min: 136, max: 164, color: 'text-orange-500', description: 'High Intensity' },
  { id: 5, name: 'Zone 5', min: 165, max: null, color: 'text-destructive', description: 'Peak Performance' }
];

export function WorkoutPlayer2({ workout, onClose }: WorkoutPlayer2Props) {
  const [isPaused, setIsPaused] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(workout.exercises[0]?.duration || 0);
  const [heartRate, setHeartRate] = useState(75);
  const [maxHeartRate, setMaxHeartRate] = useState(75);
  const [avgHeartRate, setAvgHeartRate] = useState(75);
  const [strain, setStrain] = useState(0);
  const [heartRateHistory, setHeartRateHistory] = useState<number[]>([]);
  const [workoutData, setWorkoutData] = useState<any>(null);
  const [showReport, setShowReport] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showConfirmEnd, setShowConfirmEnd] = useState(false);
  const [showTarget, setShowTarget] = useState(true);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (!isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            if (currentExerciseIndex < workout.exercises.length - 1) {
              setCurrentExerciseIndex(prev => prev + 1);
              return workout.exercises[currentExerciseIndex + 1].duration;
            }
            handleEnd();
            return 0;
          }
          return time - 1;
        });

        // Update heart rate
        setHeartRate(prev => {
          const newHeartRate = Math.round(simulateHeartRate(prev, strain));
          setHeartRateHistory(history => [...history, newHeartRate]);
          return newHeartRate;
        });

        // Update max heart rate
        setMaxHeartRate(prev => Math.max(prev, heartRate));

        // Update average heart rate
        setAvgHeartRate(prev => {
          const total = heartRateHistory.reduce((sum, hr) => sum + hr, heartRate);
          return Math.round(total / (heartRateHistory.length + 1));
        });

        // Update strain
        setStrain(prev => {
          const increment = Math.random() * 0.1;
          return Math.min(prev + increment, 21);
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isPaused, timeLeft, currentExerciseIndex, workout.exercises, heartRate, strain, heartRateHistory]);

  const handleEnd = () => {
    const duration = heartRateHistory.length;
    const data = {
      heartRateData: heartRateHistory,
      strain,
      duration: Math.round(duration / 60),
      activityType: workout.name
    };

    setWorkoutData(data);
    setShowReport(true);
  };

  if (showReport && workoutData) {
    return (
      <ActivityReport
        initialHeartRate={workoutData.heartRateData[0]}
        duration={workoutData.duration}
        activityType={workoutData.activityType}
        strain={workoutData.strain}
        onClose={onClose}
      />
    );
  }

  const currentExercise = workout.exercises[currentExerciseIndex];

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-background/50 backdrop-blur-sm border-b border-border p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">{workout.name}</h1>
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <WorkoutTimer
                  timeLeft={timeLeft}
                  totalTime={currentExercise.duration}
                  isPaused={isPaused}
                  minimal
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowTarget(!showTarget)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showTarget ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setShowConfirmEnd(true)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 space-y-6">
          {/* Current Exercise Banner */}
          <div className="bg-primary/10 rounded-lg p-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="font-medium">{currentExercise.name}</div>
              <div className="text-muted-foreground">{currentExercise.intensity}</div>
            </div>
            {currentExercise.description && (
              <div className="text-xs text-muted-foreground mt-1">{currentExercise.description}</div>
            )}
          </div>

          {/* Strain and Heart Rate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Live Strain */}
            <div className="flex flex-col items-center justify-center">
              <div className="relative w-48 h-48">
                <CircularProgress
                  value={strain}
                  maxValue={21}
                  size={192}
                  strokeWidth={16}
                  color="primary"
                  label="Live Strain"
                  targetValue={showTarget ? workout.targetStrain : undefined}
                  animated
                />
              </div>
            </div>

            {/* Heart Rate Stats */}
            <div className="flex flex-col justify-center space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">{heartRate}</div>
                  <div className="text-xs text-muted-foreground">Current HR</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{avgHeartRate}</div>
                  <div className="text-xs text-muted-foreground">Avg HR</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{maxHeartRate}</div>
                  <div className="text-xs text-muted-foreground">Max HR</div>
                </div>
              </div>

              <HeartRateZoneMeter
                currentHeartRate={heartRate}
                zones={heartRateZones}
              />
            </div>
          </div>

          {/* Controls */}
          <WorkoutControls
            isPaused={isPaused}
            onPauseToggle={() => setIsPaused(!isPaused)}
            onSkip={currentExerciseIndex < workout.exercises.length - 1 ? () => {
              setCurrentExerciseIndex(prev => prev + 1);
              setTimeLeft(workout.exercises[currentExerciseIndex + 1].duration);
            } : undefined}
            onEnd={() => setShowConfirmEnd(true)}
          />

          {/* Exercise Progress */}
          <WorkoutProgress
            exercises={workout.exercises}
            currentExerciseIndex={currentExerciseIndex}
          />
        </div>

        {/* End Workout Confirmation */}
        {showConfirmEnd && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="theme-card p-6 max-w-md w-full mx-4"
            >
              <div className="text-center space-y-6">
                <h2 className="text-2xl font-bold">End Workout?</h2>
                <div className="space-y-4">
                  <button
                    onClick={handleEnd}
                    className="w-full theme-button py-3 rounded-lg"
                  >
                    Save & End Workout
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full bg-destructive/20 text-destructive hover:bg-destructive/30 transition-colors py-3 rounded-lg"
                  >
                    Discard Workout
                  </button>
                  <button
                    onClick={() => setShowConfirmEnd(false)}
                    className="w-full bg-muted hover:bg-muted/70 transition-colors py-3 rounded-lg"
                  >
                    Continue Workout
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}